/**
 * StartHere — bloco "Comece por aqui" baseado nos gaps do ESCALA.
 *
 * Mostra 2-3 letras com gap real + 3 templates priorizados pra atacar cada
 * gap. Cada template é um botão que leva pra NovaTarefa pré-selecionada,
 * eliminando a paralisia do "comece criando uma tarefa" no estado vazio.
 *
 * 3 estados:
 *  - sem assessment → CTA pra fazer ESCALA
 *  - com assessment + recomendações → grid de gaps com tarefas
 *  - assessment todo saudável (raro) → mensagem de manutenção
 */
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight, Sparkles, Compass, Wrench, Lock } from "lucide-react";
import type {
  EscalaTaskRecommendation,
  RecommendationCategory,
  RecommendationStatus,
} from "@/api/escala";
import { getTemplateMeta, PHASE_COLORS } from "@/lib/templateMeta";

const STATUS_LABELS: Record<RecommendationStatus, { text: string; bg: string; fg: string }> = {
  gap_critical: { text: "crítico", bg: "rgba(204,51,68,0.12)", fg: "#cc3344" },
  gap_atacar: { text: "atacar", bg: "rgba(255,112,0,0.12)", fg: "#fe7000" },
  saudavel: { text: "saudável", bg: "rgba(33,168,109,0.12)", fg: "#21a86d" },
};

const CATEGORY_META: Record<
  RecommendationCategory,
  { label: string; icon: typeof Compass; color: string }
> = {
  diagnóstico: { label: "diagnóstico", icon: Compass, color: "var(--nd-orange)" },
  ataque: { label: "ataque", icon: Sparkles, color: "var(--nd-orange-pun)" },
  estrutura: { label: "estrutura", icon: Wrench, color: "var(--wf-ink-2)" },
};

export function StartHere({
  hasAssessment,
  recommendations,
}: {
  hasAssessment: boolean;
  recommendations: EscalaTaskRecommendation[];
}) {
  if (!hasAssessment) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        style={{
          background: "linear-gradient(135deg, #0d1526 0%, #1a2540 100%)",
          color: "#fff",
          padding: "28px 32px",
          borderRadius: 14,
          marginBottom: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              color: "var(--nd-orange)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 8,
            }}
          >
            <Compass size={13} strokeWidth={2} />
            Comece por aqui
          </div>
          <h2
            className="wf-display"
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              marginBottom: 8,
              lineHeight: 1.2,
            }}
          >
            Faça o diagnóstico ESCALA
          </h2>
          <p style={{ fontSize: 13, opacity: 0.78, lineHeight: 1.55, maxWidth: 540 }}>
            30 perguntas que mapeiam onde sua empresa está nas 6 letras (Estruturar,
            Significar, Captar, Ativar, Lucrar, Adesão). A partir do resultado, sua
            equipe propõe as próximas tarefas certas pra atacar o gap real.
          </p>
        </div>
        <Link
          to="/escala"
          className="wf-btn wf-btn--primary"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          Fazer agora <ArrowRight size={13} strokeWidth={2} color="#fff" />
        </Link>
      </motion.section>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  // Caso "tudo saudável" — só letras com score >= 60. Mostra parabéns + manutenção.
  const allHealthy = recommendations.every((r) => r.status === "saudavel");

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{ marginBottom: 24 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: "rgba(255,112,0,0.12)",
            color: "var(--nd-orange)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Compass size={16} strokeWidth={2} />
        </div>
        <div style={{ flex: 1 }}>
          <h2
            className="wf-display"
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            Comece por aqui
          </h2>
          <p style={{ fontSize: 12, color: "var(--wf-ink-3)", margin: 0, marginTop: 2 }}>
            {allHealthy
              ? "Sua ESCALA está saudável — mantenha o ritmo nas tarefas abaixo."
              : "Tarefas escolhidas pelos gaps reais do seu ESCALA. Ataca o que destrava primeiro."}
          </p>
        </div>
        <Link
          to="/escala"
          style={{
            fontSize: 11,
            color: "var(--wf-ink-3)",
            textDecoration: "none",
            fontFamily: "var(--wf-font-body)",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          ver radar completo <ArrowRight size={11} strokeWidth={2} />
        </Link>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        {recommendations.map((rec) => (
          <GapColumn key={rec.letter} rec={rec} />
        ))}
      </div>
    </motion.section>
  );
}

function GapColumn({ rec }: { rec: EscalaTaskRecommendation }) {
  const status = STATUS_LABELS[rec.status];
  // Cor da letra ESCALA (degradê laranja → azul ND). Substitui as cores
  // genéricas de status que ficavam todas iguais entre letras diferentes.
  const phaseColor = PHASE_COLORS[rec.letter] ?? PHASE_COLORS.C;
  const accent = phaseColor!.primary;
  const accentSoft = phaseColor!.soft;
  const scoreLabel = rec.letter === "A2" ? "A²" : rec.letter;

  return (
    <div
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 12,
        borderTop: `3px solid ${accent}`,
        boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          {/* Letra ESCALA grande na cor da fase */}
          <div
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 11,
              background: accentSoft,
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--wf-font-display)",
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1,
            }}
          >
            {scoreLabel}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              className="wf-display"
              style={{
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "-0.015em",
                margin: 0,
                lineHeight: 1.15,
                color: "var(--wf-ink)",
              }}
            >
              {rec.letterName}
            </h3>
            <div
              style={{
                fontSize: 11,
                color: "var(--wf-ink-3)",
                marginTop: 2,
              }}
            >
              Score atual ·{" "}
              <span style={{ color: status.fg, fontWeight: 700 }}>
                {Math.round(rec.score)}/100
              </span>
            </div>
          </div>
        </div>
        <span
          style={{
            padding: "3px 9px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            background: status.bg,
            color: status.fg,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            flexShrink: 0,
          }}
        >
          {status.text}
        </span>
      </header>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {rec.templates.map((tpl) => (
          <TemplateRow key={tpl.templateKey} tpl={tpl} accent={accent} accentSoft={accentSoft} />
        ))}
      </ul>
    </div>
  );
}

function TemplateRow({
  tpl,
  accent,
  accentSoft,
}: {
  tpl: EscalaTaskRecommendation["templates"][number];
  /** Cor da letra ESCALA — herdada do GapColumn pai */
  accent: string;
  accentSoft: string;
}) {
  const meta = getTemplateMeta(tpl.templateKey);
  const TemplateIcon = meta?.icon ?? Sparkles;
  const cat = CATEGORY_META[tpl.category];
  const targetUrl = tpl.isLocked
    ? "/integracoes"
    : `/nova-tarefa?template=${encodeURIComponent(tpl.templateKey)}`;

  return (
    <li>
      <Link
        to={targetUrl}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: 10,
          background: "var(--wf-bg)",
          border: "1px solid var(--wf-ink-5)",
          borderRadius: 10,
          textDecoration: "none",
          color: "inherit",
          opacity: tpl.isLocked ? 0.7 : 1,
          transition: "border-color 150ms, transform 150ms",
        }}
        onMouseEnter={(e) => {
          if (tpl.isLocked) return;
          e.currentTarget.style.borderColor = accent;
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--wf-ink-5)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {/* Ícone do template específico (não da categoria) na cor da letra */}
        <div
          style={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: 8,
            background: accentSoft,
            color: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TemplateIcon size={14} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 3,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--wf-ink)",
                lineHeight: 1.2,
                letterSpacing: "-0.005em",
              }}
            >
              {meta?.label ?? tpl.templateKey}
            </span>
            <span
              style={{
                fontSize: 9,
                color: "var(--wf-ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 600,
              }}
            >
              {cat.label}
            </span>
          </div>
          <p
            style={{
              fontSize: 11,
              color: "var(--wf-ink-2)",
              lineHeight: 1.4,
              margin: 0,
              marginBottom: 3,
            }}
          >
            {tpl.reason.why}
          </p>
          <p
            style={{
              fontSize: 10,
              color: "var(--wf-ink-3)",
              lineHeight: 1.4,
              margin: 0,
              fontStyle: "italic",
            }}
          >
            → {tpl.reason.expectedOutcome}
          </p>
          {tpl.isLocked && (
            <div
              style={{
                marginTop: 8,
                padding: "5px 8px",
                background: accentSoft,
                border: `1px dashed ${accent}`,
                borderRadius: 6,
                fontSize: 10,
                color: accent,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Lock size={10} strokeWidth={2} />
              Conecte {tpl.requiresIntegrationKey} pra liberar
            </div>
          )}
        </div>
        {tpl.isLocked ? (
          <Lock
            size={14}
            strokeWidth={2}
            color="var(--wf-ink-3)"
            style={{ flexShrink: 0, marginTop: 6 }}
          />
        ) : (
          <ArrowRight
            size={14}
            strokeWidth={2}
            color={accent}
            style={{ flexShrink: 0, marginTop: 6 }}
          />
        )}
      </Link>
    </li>
  );
}
